import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useServices, useServiceCategories, useBusiness } from "@/hooks/api-hooks";
import { setWebTitle, formatPrice, formatDayShort } from "@/lib/format-utils";
import { localTenant } from "@/services/session-service";
import BusinessSelector from "@/components/business-selector";
import { groupServicesByCategoryAndProvider } from "@/lib/service-grouping";
import { Business, Service, ServiceSellPrice } from "@turizi/shared";
import { Button, Loader, Popover, ActionIcon } from "@mantine/core";
import { TagIcon } from "@/lib/tag-utils";
import ServiceTariffCard from "@/components/service-tariff-card";
import TariffsFilters from "@/components/tariffs-filters";
import { ServiceToString } from "@/lib/models-utils";
import {
    IconPin,
    IconTrash,
    IconCopy,
    IconPlus,
    IconMinus,
    IconX,
    IconFilter,
    IconChevronDown,
    IconChevronRight,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconBuildingStore,
    IconMapPin,
} from "@tabler/icons-react";
import { ServiceTitleStatic } from "@/components/services/service-title";
import SearchInput from "@/components/search-input";
import { multiWordSearch } from "@/utils/search-utils";
import {
    PinnedTariffItem,
    getPinnedItemPriceInfo,
    calculatePinnedTotals,
    copySummaryToClipboard
} from "@/lib/tariff-summary";
import { ALL_TEMPLATES } from "@/types/templates";


export default function TariffsPage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();
    const tenant_id = tenant?.id;

    setWebTitle(t('tariffs'));

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedAttrs, setSelectedAttrs] = useState<Record<string, string[]>>({});
    const [filtersOpened, setFiltersOpened] = useState(false);
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const [collapsedCities, setCollapsedCities] = useState<Set<string>>(new Set());

    const activeFiltersCount = useMemo(() => {
        let count = selectedCategories.length;
        Object.values(selectedAttrs).forEach(values => {
            if (values && values.length > 0) {
                count += values.length;
            }
        });
        return count;
    }, [selectedCategories, selectedAttrs]);
    const [searchParams, setSearchParams] = useSearchParams();
    const businessIdParam = searchParams.get('business_id');

    const [selectedClient, setSelectedClient] = useState<Business | null>(null);
    const [pinnedItems, setPinnedItems] = useState<PinnedTariffItem[]>([]);

    const pinnedIds = useMemo(() => pinnedItems.map(item => item.id), [pinnedItems]);

    const {
        data: businessDetailData
    } = useBusiness(businessIdParam, {
        enabled: !!businessIdParam
    });
    const loadedBusiness = businessDetailData?.data;

    useEffect(() => {
        if (!businessIdParam) {
            if (selectedClient !== null) {
                setSelectedClient(null);
            }
            return;
        }

        if (loadedBusiness && String(loadedBusiness.id) === businessIdParam) {
            if (!selectedClient || selectedClient.id !== loadedBusiness.id) {
                setSelectedClient(loadedBusiness);
            }
        }
    }, [businessIdParam, loadedBusiness, selectedClient]);

    const handleSelectClient = (client: Business) => {
        setSelectedClient(client);
        if (client) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('business_id', String(client.id));
                return next;
            }, { replace: true });
        }
    };

    const {
        isLoading: isLoadingServices,
        data: servicesData
    } = useServices(
        {
            publish_price: true
        }
    );

    const {
        isLoading: isLoadingCategories,
        data: categoriesData
    } = useServiceCategories(
        {
            tenant_id
        }
    );

    const servicesWithSearchStr = useMemo(() => {
        return (servicesData?.data || []).map(service => ({
            service,
            searchStr: ServiceToString(service, t)
        }));
    }, [servicesData, t]);

    // Get list of services filtered by search and category
    const baseFilteredServices = useMemo(() => {
        let list = servicesData?.data || [];

        // Category filter
        if (selectedCategories.length > 0) {
            list = list.filter(s => s.category && selectedCategories.includes(s.category));
        }

        // Search query filter
        if (searchQuery) {
            const query = searchQuery.trim();
            const filteredWithSearch = servicesWithSearchStr
                .filter(({ service, searchStr }) => {
                    const title = multiWordSearch(searchStr, query) || false;
                    const _id = service.id.toString() === query;
                    return title || _id;
                })
                .map(item => item.service);

            const filteredIds = new Set(filteredWithSearch.map(s => s.id));
            list = list.filter(s => filteredIds.has(s.id));
        }

        return list;
    }, [servicesData, selectedCategories, searchQuery, servicesWithSearchStr]);

    // Master sequence of attribute keys defined in service/business templates
    const templateAttrOrder = useMemo(() => {
        const order: string[] = [];
        Object.values(ALL_TEMPLATES).forEach(tmpl => {
            tmpl.self_attrs?.forEach(attr => {
                if (attr.key && !order.includes(attr.key)) {
                    order.push(attr.key);
                }
            });
        });
        return order;
    }, []);

    const getServiceAttrs = (service: Service) => {
        const attrs = [...(service.self_attrs || [])];
        const providers: any[] = [];
        if (service.providers) {
            if (Array.isArray(service.providers)) {
                providers.push(...service.providers);
            } else if (typeof service.providers === 'object') {
                providers.push(...Object.values(service.providers));
            }
        }
        if ((service as any).provider) providers.push((service as any).provider);
        if ((service as any).selected_provider) providers.push((service as any).selected_provider);

        providers.forEach(p => {
            if (p?.self_attrs && Array.isArray(p.self_attrs)) {
                p.self_attrs.forEach((pAttr: any) => {
                    if (pAttr.key && pAttr.value !== undefined && pAttr.value !== null && pAttr.value !== '') {
                        if (!attrs.some(a => a.key === pAttr.key)) {
                            attrs.push(pAttr);
                        }
                    }
                });
            }
        });

        return attrs;
    };

    // Gather all possible self_attr keys from the base filtered list, ordered by template sequence
    const attrKeys = useMemo(() => {
        const keys = new Set<string>();
        baseFilteredServices.forEach(service => {
            const attrs = getServiceAttrs(service);
            attrs.forEach(attr => {
                if (attr.key && attr.value !== undefined && attr.value !== null && attr.value !== '') {
                    keys.add(attr.key);
                }
            });
        });
        return Array.from(keys).sort((a, b) => {
            const idxA = templateAttrOrder.indexOf(a);
            const idxB = templateAttrOrder.indexOf(b);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return a.localeCompare(b);
        });
    }, [baseFilteredServices, templateAttrOrder]);

    // For each key, compute the options from services filtered by other attributes, keeping unavailable options marked as disabled
    const attrOptionsMap = useMemo(() => {
        const map: Record<string, { value: string; label: string; disabled?: boolean }[]> = {};

        attrKeys.forEach(key => {
            // 1. All unique values for this key across baseFilteredServices
            const allValues = new Set<string>();
            baseFilteredServices.forEach(s => {
                const attrs = getServiceAttrs(s);
                const attr = attrs.find(a => a.key === key);
                if (attr && attr.value !== undefined && attr.value !== null && attr.value !== '') {
                    allValues.add(String(attr.value).trim());
                }
            });

            // 2. Filter baseFilteredServices by all selected attributes EXCEPT the current key
            let list = baseFilteredServices;
            Object.entries(selectedAttrs).forEach(([otherKey, selectedValues]) => {
                if (otherKey !== key && selectedValues && selectedValues.length > 0) {
                    list = list.filter(s => {
                        const attrs = getServiceAttrs(s);
                        const attr = attrs.find(a => a.key === otherKey);
                        const val = attr && attr.value !== undefined && attr.value !== null && attr.value !== '' ? String(attr.value).trim() : '';
                        return val && selectedValues.includes(val);
                    });
                }
            });

            // 3. Extract unique values in filtered list
            const availableValues = new Set<string>();
            list.forEach(s => {
                const attrs = getServiceAttrs(s);
                const attr = attrs.find(a => a.key === key);
                if (attr && attr.value !== undefined && attr.value !== null && attr.value !== '') {
                    availableValues.add(String(attr.value).trim());
                }
            });

            map[key] = Array.from(allValues).sort().map(v => ({
                value: v,
                label: v,
                disabled: !availableValues.has(v)
            }));
        });

        return map;
    }, [attrKeys, baseFilteredServices, selectedAttrs]);

    const services = useMemo(() => {
        let list = baseFilteredServices;

        // Custom attribute filters
        Object.entries(selectedAttrs).forEach(([key, selectedValues]) => {
            if (selectedValues && selectedValues.length > 0) {
                list = list.filter(s => {
                    const attrs = getServiceAttrs(s);
                    const attr = attrs.find(a => a.key === key);
                    const val = attr && attr.value !== undefined && attr.value !== null && attr.value !== '' ? String(attr.value).trim() : '';
                    return val && selectedValues.includes(val);
                });
            }
        });

        return list;
    }, [baseFilteredServices, selectedAttrs]);

    const sortedCategories = useMemo(() => {
        return (categoriesData?.data || [])
            .filter(Boolean)
            .sort((a, b) => {
                const rankA = a.rank ?? Infinity;
                const rankB = b.rank ?? Infinity;
                return rankA - rankB;
            });
    }, [categoriesData]);

    const categoryGroups = useMemo(() => {
        const sortedServices = [...services].sort((a, b) => (a.rank || 0) - (b.rank || 0));
        return groupServicesByCategoryAndProvider(sortedServices, sortedCategories);
    }, [services, sortedCategories]);

    const toggleCategory = (category: string) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const toggleCity = (category: string, city: string) => {
        const key = `${category}:${city}`;
        setCollapsedCities(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const areAllExpanded = useMemo(() => {
        return collapsedCategories.size === 0 && collapsedCities.size === 0;
    }, [collapsedCategories, collapsedCities]);

    const handleToggleExpandAll = () => {
        if (areAllExpanded) {
            setCollapsedCategories(new Set(categoryGroups.map(g => g.category)));
        } else {
            setCollapsedCategories(new Set());
            setCollapsedCities(new Set());
        }
    };

    const isLoading = isLoadingServices || isLoadingCategories;

    const totals = useMemo(() => {
        return calculatePinnedTotals(pinnedItems, selectedClient);
    }, [pinnedItems, selectedClient]);

    const handleTogglePin = (service: Service, pricePeriod: ServiceSellPrice, tierKey: string) => {
        const id = `${service.id}-${pricePeriod.id}-${tierKey}`;
        setPinnedItems(prev => {
            const exists = prev.some(item => item.id === id);
            if (exists) {
                return prev.filter(item => item.id !== id);
            } else {
                const minPax = service.min_pax_per_booking || 0;
                const initialQty = minPax > 1 ? minPax : 1;
                return [...prev, {
                    id,
                    service,
                    pricePeriod,
                    tierKey,
                    quantity: initialQty
                }];
            }
        });
    };

    const handleIncrement = (id: string) => {
        setPinnedItems(prev => prev.map(item => {
            if (item.id === id) {
                const maxPax = item.service.max_pax_per_booking || 0;
                if (maxPax > 0 && item.quantity >= maxPax) {
                    return item;
                }
                return { ...item, quantity: item.quantity + 1 };
            }
            return item;
        }));
    };

    const handleDecrement = (id: string) => {
        setPinnedItems(prev => prev.map(item => {
            if (item.id === id) {
                const minPax = item.service.min_pax_per_booking || 0;
                const minAllowed = minPax > 1 ? minPax : 1;
                return { ...item, quantity: Math.max(minAllowed, item.quantity - 1) };
            }
            return item;
        }));
    };

    const handleIncrementNights = (id: string) => {
        setPinnedItems(prev => prev.map(item =>
            item.id === id
                ? { ...item, nights: (item.nights || 1) + 1 }
                : item
        ));
    };

    const handleDecrementNights = (id: string) => {
        setPinnedItems(prev => prev.map(item => {
            if (item.id === id) {
                return { ...item, nights: Math.max(1, (item.nights || 1) - 1) };
            }
            return item;
        }));
    };

    const handleRemovePinned = (id: string) => {
        setPinnedItems(prev => prev.filter(item => item.id !== id));
    };

    const handleClearAllPinned = () => {
        setPinnedItems([]);
    };

    const handleCopySummary = () => {
        copySummaryToClipboard(pinnedItems, selectedClient, totals, t);
    };


    return (
        <div className="panel-content space-y-6">
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader color="black" size="lg" />
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-7 space-y-10">
                        <div className="flex gap-3 items-center flex-wrap sm:flex-nowrap">
                            <div className="flex-grow">
                                <SearchInput
                                    onSearch={setSearchQuery}
                                    size="md"
                                />
                            </div>
                            <Popover
                                opened={filtersOpened}
                                onChange={setFiltersOpened}
                                withArrow
                                shadow="xl"
                                width={520}
                            >
                                <Popover.Target>
                                    <Button
                                        variant={activeFiltersCount > 0 ? "light" : "subtle"}
                                        color={activeFiltersCount > 0 ? "red" : "black"}
                                        leftSection={<IconFilter size={16} />}
                                        onClick={() => setFiltersOpened(prev => !prev)}
                                        size="sm"
                                    >
                                        {t('filters')} ({activeFiltersCount})
                                    </Button>
                                </Popover.Target>
                                <Popover.Dropdown onClick={e => e.stopPropagation()}>
                                    <TariffsFilters
                                        categoriesData={categoriesData?.data || []}
                                        selectedCategories={selectedCategories}
                                        setSelectedCategories={setSelectedCategories}
                                        attrKeys={attrKeys}
                                        attrOptionsMap={attrOptionsMap}
                                        selectedAttrs={selectedAttrs}
                                        setSelectedAttrs={setSelectedAttrs}
                                        activeFiltersCount={activeFiltersCount}
                                        onClearAll={() => {
                                            setSelectedCategories([]);
                                            setSelectedAttrs({});
                                        }}
                                    />
                                </Popover.Dropdown>
                            </Popover>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="lg"
                                radius="xl"
                                onClick={handleToggleExpandAll}
                                title={areAllExpanded ? t('collapse_all') : t('expand_all')}
                                className="shrink-0 cursor-pointer"
                            >
                                {areAllExpanded ? <IconArrowsMinimize size={20} /> : <IconArrowsMaximize size={20} />}
                            </ActionIcon>
                        </div>
                        {categoryGroups.map(({ category, cities }) => {
                            const isCategoryCollapsed = searchQuery.trim().length > 0 ? false : collapsedCategories.has(category);
                            const totalCategoryServices = cities.reduce(
                                (sum, c) => sum + c.providers.reduce((pSum, p) => pSum + p.services.length, 0),
                                0
                            );

                            return (
                                <div key={category} className="pb-6 border-b border-gray-100 last:border-0">
                                    <button
                                        type="button"
                                        onClick={() => toggleCategory(category)}
                                        className="w-full text-left font-bold text-2xl text-gray-800 py-2 capitalize flex items-center justify-between group cursor-pointer focus:outline-none hover:text-blue-600 transition-colors"
                                    >
                                        <div className={`
                                            flex items-center gap-2
                                            ${isCategoryCollapsed ? 'text-blue-600' : 'text-red-700'}
                                            `}>
                                            <TagIcon name={category} size="sm" hideText />
                                            <span className="text-xl font-bold">{t(category)}</span>
                                            <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full normal-case">
                                                {totalCategoryServices}
                                            </span>
                                        </div>
                                        <div className="text-gray-400 group-hover:text-blue-600 transition-transform duration-200">
                                            {isCategoryCollapsed ? (
                                                <IconChevronRight size={22} />
                                            ) : (
                                                <IconChevronDown size={22} />
                                            )}
                                        </div>
                                    </button>

                                    {!isCategoryCollapsed && (
                                        <div className="mt-4 space-y-6">
                                            {cities.map(({ city, providers }) => {
                                                const cityKey = `${category}:${city}`;
                                                const isCityCollapsed = searchQuery.trim().length > 0 ? false : collapsedCities.has(cityKey);

                                                return (
                                                    <div key={city} className="last:mb-0">
                                                        {city !== 'Other' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleCity(category, city)}
                                                                className="w-full text-left font-semibold text-lg text-gray-700 py-1 mb-3 border-b border-gray-100 flex items-center justify-between group cursor-pointer focus:outline-none hover:text-blue-600 transition-colors"
                                                            >
                                                                <div className={`
                                                                        flex items-center gap-2 text-lg font-bold
                                                                        ${isCityCollapsed ? 'text-blue-600' : 'text-red-700'}
                                                                    `}>
                                                                    <IconMapPin size={18} />
                                                                    {city}
                                                                </div>
                                                                <div className="text-gray-400 group-hover:text-blue-600 transition-transform duration-200">
                                                                    {isCityCollapsed ? (
                                                                        <IconChevronRight size={18} />
                                                                    ) : (
                                                                        <IconChevronDown size={18} />
                                                                    )}
                                                                </div>
                                                            </button>
                                                        )}

                                                        {(!isCityCollapsed || city === 'Other') && (
                                                            <div className="space-y-6">
                                                                {providers.map(({ providerId, provider, services: providerServices }) => (
                                                                    <div key={providerId} className="first:mt-0">
                                                                        {provider && (
                                                                            <div className="mb-4 bg-amber-50 border-amber-200 border p-2 rounded-lg flex items-center justify-between flex-wrap gap-2">
                                                                                <div>
                                                                                    <div className="flex items-center gap-2 font-semibold text-lg text-gray-900">
                                                                                        <TagIcon name={provider.category} size="sm" hideText noBackground />
                                                                                        {provider.name}
                                                                                    </div>
                                                                                    {provider.observations && (
                                                                                        <p className="text-xs text-gray-600 whitespace-pre-line mt-1">
                                                                                            {provider.observations}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        <div className="space-y-2">
                                                                            {providerServices.map(service => {
                                                                                const hasAnyPinned = pinnedItems.some(item => item.service.id === service.id);
                                                                                return (
                                                                                    <div key={service.id} className="relative group/card">
                                                                                        <ServiceTariffCard
                                                                                            onSelect={handleTogglePin}
                                                                                            service={service}
                                                                                            client={selectedClient?.type === 'business' ? selectedClient.tenant_business : undefined}
                                                                                            pinnedIds={pinnedIds}
                                                                                            className={
                                                                                                hasAnyPinned
                                                                                                    ? 'text-sm solid-box-border-positive'
                                                                                                    : 'text-sm solid-box-border-hover'
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Simulator Panel Column */}
                    <div className="lg:col-span-5 sticky top-16 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="flex gap-2 justify-between w-full p-4 bg-green-50 border-b border-green-200">
                            <div className="flex-1">
                                <BusinessSelector
                                    selected={selectedClient}
                                    onSelect={handleSelectClient}
                                    className="font-bold"
                                />
                            </div>
                            <div>
                                <div className="font-bold text-gray-800 text-sm md:text-base leading-none">
                                    {t('budget')}
                                </div>
                                <div className="text-xxs text-gray-500">
                                    {pinnedItems.length} {t('pinned_services').toLowerCase()}
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        {pinnedItems.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center justify-center">
                                <div className="bg-gray-50 p-3 rounded-full text-gray-400 mb-3 border border-gray-100">
                                    <IconPin size={24} stroke={1} />
                                </div>
                                <h4 className="font-semibold text-gray-700 text-sm mb-1">{t('no_pinned_services')}</h4>
                                <p className="text-xs text-gray-400 max-w-xs">{t('click_to_pin')}</p>
                            </div>
                        ) : (
                            <div>
                                <div className="p-4 max-h-[400px] overflow-y-auto divide-y divide-gray-100">
                                    {pinnedItems.map(item => {
                                        const priceInfo = getPinnedItemPriceInfo(item, selectedClient?.type === 'business' ? selectedClient.tenant_business : undefined);
                                        const isPaxBased = !item.service.price_by || item.service.price_by === 'pax' || item.service.price_by === 'night_and_pax';
                                        const isNightBased = item.service.price_by === 'night' || item.service.price_by === 'night_and_pax';
                                        const minPax = item.service.min_pax_per_booking || 0;
                                        const maxPax = item.service.max_pax_per_booking || 0;
                                        const minAllowed = minPax > 1 ? minPax : 1;
                                        const effectiveQty = isPaxBased ? Math.max(item.quantity, minAllowed) : 1;
                                        const effectiveNights = isNightBased ? Math.max(item.nights || 1, 1) : 1;
                                        const tierLabel = item.tierKey === 'default' ? t('general')
                                            : item.tierKey === 'foreigner' ? t('foreigner')
                                                : item.tierKey === 'child' ? t('children')
                                                    : item.tierKey === 'senior' ? (item.service.show_retired ? t('retired_plural') : t('seniors'))
                                                        : t('infants');

                                        return (
                                            <div key={item.id} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between gap-3 group/item">
                                                {/* Left: Controllers, Category Icon, Name */}
                                                <div className="flex items-start gap-2.5 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        {/* Quantity Controller - for pax based services */}
                                                        {isPaxBased && (
                                                            <div className="flex flex-col items-center gap-1 border border-gray-200 rounded-md bg-gray-50/50 p-1 flex-shrink-0" title={t('pax')}>
                                                                <button
                                                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                                                                    onClick={() => handleIncrement(item.id)}
                                                                    disabled={maxPax > 0 && effectiveQty >= maxPax}
                                                                >
                                                                    <IconPlus size={10} stroke={3} />
                                                                </button>
                                                                <span className="text-xs font-bold text-gray-700 px-1">
                                                                    {effectiveQty}
                                                                </span>
                                                                <button
                                                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                                                                    onClick={() => handleDecrement(item.id)}
                                                                    disabled={item.quantity <= minAllowed}
                                                                >
                                                                    <IconMinus size={10} stroke={3} />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Nights Controller - for night based services */}
                                                        {isNightBased && (
                                                            <div className="flex flex-col items-center gap-1 border border-indigo-200/60 rounded-md bg-indigo-50/30 p-1 flex-shrink-0" title={t('nights')}>
                                                                <button
                                                                    className="text-gray-400 hover:text-indigo-600 transition-colors"
                                                                    onClick={() => handleIncrementNights(item.id)}
                                                                >
                                                                    <IconPlus size={10} stroke={3} />
                                                                </button>
                                                                <span className="text-xs font-bold text-indigo-700 px-1">
                                                                    {effectiveNights}
                                                                </span>
                                                                <button
                                                                    className="text-gray-400 hover:text-indigo-600 disabled:opacity-30 transition-colors"
                                                                    onClick={() => handleDecrementNights(item.id)}
                                                                    disabled={(item.nights || 1) <= 1}
                                                                >
                                                                    <IconMinus size={10} stroke={3} />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {!isPaxBased && !isNightBased && (
                                                            <div className="flex items-center justify-center border border-gray-200 rounded-md bg-gray-50/50 p-1.5 flex-shrink-0">
                                                                <TagIcon name={item.service.category} hideText size="xs" noBackground />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex-1 min-w-0 pt-0.5">
                                                        <div className="flex items-center gap-1 flex-wrap">
                                                            {(isPaxBased || isNightBased) && <TagIcon name={item.service.category} hideText size="xxs" noBackground />}
                                                            <div className="font-medium text-xs text-gray-800">
                                                                {ServiceTitleStatic(item.service)} ({tierLabel})
                                                            </div>
                                                            {(isPaxBased || minPax > 0 || maxPax > 0) && (
                                                                <span className="font-medium text-[10px] text-amber-700">
                                                                    ({[
                                                                        minPax > 1 ? `${t('min_pax')}: ${minPax}` : t('no_min'),
                                                                        maxPax > 0 ? `${t('max_pax')}: ${maxPax}` : t('no_max')
                                                                    ].join(' • ')})
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(item.service.price_by || isNightBased || (priceInfo && (priceInfo.start_day || priceInfo.end_day)) || item.pricePeriod) && (
                                                            <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                                                                {item.pricePeriod?.business_id ? (
                                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.25 rounded text-[9px] font-semibold bg-amber-50 text-amber-800 border border-amber-200/80">
                                                                        <IconBuildingStore size={10} className="text-amber-700" />
                                                                        <span>{item.pricePeriod.business?.name || "business_name_missing"}</span>
                                                                    </span>
                                                                ) : (
                                                                    null
                                                                )}
                                                                {item.service.price_by && (
                                                                    <span>{t('price_by_' + item.service.price_by)}</span>
                                                                )}
                                                                {isNightBased && (
                                                                    <span className="font-medium text-indigo-700">
                                                                        ({effectiveNights} {effectiveNights === 1 ? t('night') : t('nights')})
                                                                    </span>
                                                                )}
                                                                {priceInfo && (priceInfo.start_day || priceInfo.end_day) && (
                                                                    <>
                                                                        <span className="text-gray-300">•</span>
                                                                        <span className="text-gray-400 italic">
                                                                            {formatDayShort(priceInfo.start_day)}{priceInfo.end_day && ` - ${formatDayShort(priceInfo.end_day)}`}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Right: Prices & Delete button */}
                                                <div className="flex items-start gap-2 flex-shrink-0 text-right mt-4">
                                                    {priceInfo ? (
                                                        <div className="space-y-0.5 pt-0.5">
                                                            <div className="text-xs font-bold text-gray-800">
                                                                $ {formatPrice(priceInfo.finalUnitAmount * effectiveQty * effectiveNights)} {priceInfo.currency}
                                                            </div>
                                                            {priceInfo.discountPercentage > 0 && (
                                                                <div className="text-[9px] text-gray-500">
                                                                    $ {formatPrice(priceInfo.originalUnitAmount * effectiveQty * effectiveNights)} {priceInfo.currency}
                                                                </div>
                                                            )}
                                                            {((isPaxBased && effectiveQty > 1) || (isNightBased && effectiveNights > 1) || (isPaxBased && isNightBased)) && (
                                                                <div className="text-[9px] text-gray-400">
                                                                    ({[
                                                                        isPaxBased ? `${effectiveQty} pax` : null,
                                                                        isNightBased ? `${effectiveNights} ${effectiveNights === 1 ? t('night') : t('nights')}` : null
                                                                    ].filter(Boolean).join(' x ')} x $ {formatPrice(priceInfo.finalUnitAmount)})
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] text-gray-400 italic">{t('---')}</span>
                                                    )}

                                                    <button
                                                        onClick={() => handleRemovePinned(item.id)}
                                                        className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-all opacity-0 group-hover/item:opacity-100 focus:opacity-100"
                                                    >
                                                        <IconX size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Footer Summary */}
                                <div className="bg-gray-50/90 p-4 border-t border-gray-200 space-y-3">
                                    <div className="bg-white p-3.5 rounded-lg border border-gray-200/80 shadow-xs space-y-2">
                                        {Object.entries(totals).map(([currency, amount]) => (
                                            <div key={currency} className="flex justify-between items-center">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    {t('total')}
                                                </span>
                                                <span className="text-lg font-bold text-gray-900">
                                                    $ {formatPrice(amount)} <span className="text-xs font-medium text-gray-400">{currency}</span>
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex items-center justify-between gap-3 pt-1">
                                        <Button
                                            variant="light"
                                            color="black"
                                            size="sm"
                                            onClick={handleCopySummary}
                                            leftSection={<IconCopy size={16} />}
                                            className="flex-1"
                                        >
                                            {t('copy')} {t('budget')}
                                        </Button>
                                        {pinnedItems.length > 0 && (
                                            <Button
                                                variant="subtle"
                                                color="red"
                                                size="sm"
                                                onClick={handleClearAllPinned}
                                                leftSection={<IconTrash size={15} />}
                                            >
                                                {t('clear')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
